/**
 * Shared Annotate Dropdown Component
 *
 * Provides consistent "Annotate" dropdown across sections with:
 * - Add Comment/Note
 * - Add Tag
 * - Remove Tag
 * - (Optional) Add / Remove Scan Profile (smart-context, shown when scanProfile prop provided)
 *
 * Sections control business logic through handler props.
 * Component handles UI presentation and consistency.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <AnnotateDropdown
 *   onCommentClick={modalHandlers.openCommentModal}
 *   onTagClick={modalHandlers.openTagModal}
 *   onRemoveTagClick={modalHandlers.openRemoveTagModal}
 * />
 *
 * // With custom labels (Assets section uses "Note")
 * <AnnotateDropdown
 *   labels={{ comment: 'Add Note' }}
 *   onCommentClick={...}
 *   onTagClick={...}
 *   onRemoveTagClick={...}
 * />
 *
 * // With scan profile management
 * <AnnotateDropdown
 *   onCommentClick={...}
 *   onTagClick={...}
 *   onRemoveTagClick={...}
 *   scanProfile={{
 *     profiles,
 *     selectionState,
 *     onAdd: handleAddProfile,
 *     onRemove: handleRemoveProfile,
 *     onManage: handleManageProfiles,
 *   }}
 * />
 * ```
 *
 * @module components/actions
 */

import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { X } from 'lucide-react';
import { useMemo } from 'react';

import { ACTION_ICONS } from '@/components/actions/constants/actionIcons';
import type {
  AnnotateDropdownProps,
  AnnotateScanProfileConfig,
} from '@/components/actions/types';
import { Dropdown } from '@/components/Dropdown';
import type { MenuItemProps } from '@/components/Menu';
import { cn } from '@/utils/classname';

/**
 * Default labels for annotate actions
 */
const DEFAULT_LABELS = {
  comment: 'Add Comment',
  tag: 'Add Tag',
  removeTag: 'Remove Tag',
} as const;

/**
 * Build the scan profile menu items that sit below the tag actions.
 *
 * Logic:
 * - selectionState === 'all'  → only "Remove Scan Profile"
 * - selectionState === 'none' → only "Add Scan Profile" (with profile submenu or no-profiles message)
 * - selectionState === 'mixed'→ both
 */
function buildScanProfileItems(
  config: AnnotateScanProfileConfig
): MenuItemProps[] {
  const { profiles, selectionState, onAdd, onRemove, onManage } = config;

  const items: MenuItemProps[] = [];

  const showAdd = selectionState === 'none' || selectionState === 'mixed';
  const showRemove = selectionState === 'all' || selectionState === 'mixed';

  // Divider before scan profile section
  items.push({ label: '', type: 'divider' });

  if (showAdd) {
    if (profiles.length === 0) {
      // No profiles defined — direct link to management
      items.push({
        label: 'No profiles defined — Manage profiles…',
        icon: <ACTION_ICONS.STATUS_CHANGE className="size-4" />,
        onClick: () => onManage(),
      });
    } else {
      // Build submenu from available profiles + manage link
      const profileSubmenuItems: MenuItemProps[] = [
        ...profiles.map(p => ({
          label: p.name,
          onClick: () => onAdd(p.id),
        })),
        { label: '', type: 'divider' as const },
        {
          label: 'Manage profiles…',
          onClick: () => onManage(),
        },
      ];

      items.push({
        label: 'Add Scan Profile',
        icon: <ACTION_ICONS.STATUS_CHANGE className="size-4" />,
        submenu: profileSubmenuItems,
      });
    }
  }

  if (showRemove) {
    items.push({
      label: 'Remove Scan Profile',
      icon: <X className="size-4" />,
      onClick: () => onRemove(),
    });
  }

  return items;
}

/**
 * Annotate Dropdown Component
 *
 * Renders a dropdown with Comment, Tag, Remove Tag, and (optionally) Scan Profile actions.
 * Handlers must be provided by parent component (section-level).
 *
 * Uses the modern Dropdown + Menu pattern (floating-ui) which supports submenus.
 *
 * @param props - Component props
 * @returns Dropdown component
 */
export function AnnotateDropdown({
  onCommentClick,
  onTagClick,
  onRemoveTagClick,
  labels,
  hideActions,
  scanProfile,
  disabled = false,
  className,
  'data-testid': testId = 'annotate-dropdown',
}: AnnotateDropdownProps) {
  /**
   * Build menu items based on configuration.
   *
   * Memoized to prevent unnecessary re-renders when handlers change.
   * Only rebuilds when labels, hideActions, or scanProfile config changes.
   */
  const menuItems = useMemo<MenuItemProps[]>(() => {
    const items: MenuItemProps[] = [];

    // Comment action
    if (!hideActions?.comment) {
      items.push({
        label: labels?.comment ?? DEFAULT_LABELS.comment,
        icon: <ACTION_ICONS.COMMENT className="size-4" />,
        onClick: () => onCommentClick(),
        disabled,
      });
    }

    // Tag action
    if (!hideActions?.tag) {
      items.push({
        label: labels?.tag ?? DEFAULT_LABELS.tag,
        icon: <ACTION_ICONS.ADD_TAG className="size-4" />,
        onClick: () => onTagClick(),
        disabled,
      });
    }

    // Remove Tag action
    if (!hideActions?.removeTag) {
      items.push({
        label: labels?.removeTag ?? DEFAULT_LABELS.removeTag,
        icon: <ACTION_ICONS.REMOVE_TAG className="size-4" />,
        onClick: () => onRemoveTagClick(),
        disabled,
      });
    }

    // Scan profile actions (optional)
    if (scanProfile) {
      const scanItems = buildScanProfileItems(scanProfile);
      items.push(...scanItems);
    }

    return items;
  }, [
    labels,
    hideActions,
    scanProfile,
    disabled,
    onCommentClick,
    onTagClick,
    onRemoveTagClick,
  ]);

  return (
    <div data-testid={testId}>
      <Dropdown
        startIcon={<EllipsisHorizontalCircleIcon className="size-4" />}
        label="Annotate"
        styleType="rounded"
        size="sm"
        disabled={disabled}
        className={cn('[&_span:nth-child(3)]:hidden', className)}
        menu={{
          items: menuItems,
          placement: 'bottom-start',
          className: '!z-[150]',
        }}
      />
    </div>
  );
}
